import { Component, OnInit, Type, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { catchError, map, Observable, of } from 'rxjs';

import { ZciRouteData } from '../../app-routing.config';
import { GatekeeperService } from '../../services/gatekeeper/gatekeeper.service';
import { ClientSegmentFacadeService } from '../../services/client-segment-observer/client-segment.service';
import { AsyncPipe, NgComponentOutlet } from '@angular/common';

@Component({
    selector: 'zci-dynamic-route',
    templateUrl: './dynamic-route.component.html',
    imports: [
        AsyncPipe,
        NgComponentOutlet,
    ],
})
export class DynamicRouteComponent implements OnInit {
    private readonly gatekeeperService = inject(GatekeeperService);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly clientSegmentFacadeService = inject(ClientSegmentFacadeService);

    component$: Observable<Type<Component>>;

    ngOnInit(): void {
        this.component$ = this.getComponentBasedOnRouteData();
    }

    /**
     * Determines the component to render based on the route's data.
     * - If `dynamicClientSegmentComponent` is present in the route data, it handles the client segment scenario.
     * - If `dynamicRouteComponent` is present in the route data, it handles the gatekeeper toggle scenario.
     * - If neither is present, it throws an error indicating invalid route data.
     * 
     * @returns An observable of the component type to render.
     */
    private getComponentBasedOnRouteData(): Observable<Type<Component>> {
        const routeData: ZciRouteData = <ZciRouteData>this.activatedRoute.snapshot.data;

        const dynamicClientSegmentComponent = routeData.dynamicClientSegmentComponent;
        const dynamicRouteComponent = routeData.dynamicRouteComponent;

        if (dynamicClientSegmentComponent) {
            return this.handleClientSegmentScenario(dynamicClientSegmentComponent);
        } else if (dynamicRouteComponent) {
            return this.handleGatekeeperToggleScenario(dynamicRouteComponent);
        } else {
            throw new Error(
                `Please add the proper route data under this path: ${this.activatedRoute.snapshot.routeConfig.path}`,
            );
        }
    }

    /**
     * Handles the client segment scenario by returning the component type
     * specified in the `dynamicClientSegmentComponent` route data.
     * 
     * @param component The component type to render for the client segment scenario.
     * @returns An observable of the component type to render.
     */
    private handleClientSegmentScenario(data: any): Observable<Type<Component>> {
        const { onComponent, offComponent } = data;

        if (!(onComponent && offComponent)) {
            throw new Error(
                `Please add the proper route data under this path: ${this.activatedRoute.snapshot.routeConfig.path}`,
            );
        }

        return this.clientSegmentFacadeService.isWealthManagementClient$.pipe(
            map((isWealthManagementClient) => {
                if (isWealthManagementClient) {
                    return onComponent;
                }
                return offComponent;
            }),
            catchError(() => {
                return of(offComponent);
            }),
        );
    }

    /**
     * Handles the gatekeeper toggle scenario by checking the gatekeeper service
     * and returning the component type specified in the `dynamicRouteComponent` route data.
     * 
     * @param component The component type to render for the gatekeeper toggle scenario.
     * @returns An observable of the component type to render.
     */
    private handleGatekeeperToggleScenario(data: any): Observable<Type<Component>> {

        const { featureId, onComponent, offComponent } = data

        if (!(featureId && onComponent && offComponent)) {
            throw new Error(
                `Please add the proper route data under this path: ${this.activatedRoute.snapshot.routeConfig.path}`,
            );
        }

        return this.gatekeeperService.isToggleActive(featureId).pipe(
            map((isEnabled: boolean) => {
                return isEnabled ? onComponent : offComponent;
            }),
            catchError(() => {
                return of(offComponent);
            }),
        );
    }
}
